import React, { useEffect, useState, useCallback } from "react";
import { Target, Plus, Trash2, ExternalLink, Edit2, X, Star, TrendingUp, TrendingDown, Minus } from "lucide-react";
import { toast } from "sonner";
import api, { apiError, arr } from "@/lib/api";
import PageHeader from "@/components/common/PageHeader";
import EmptyState from "@/components/common/EmptyState";
import { Button } from "@/components/ui/button";

const emptyForm = {
  name: "",
  gbpUrl: "",
  category: "",
  rating: "",
  reviewCount: "",
  postFrequency: "",
  responseRate: "",
  notes: "",
};

function StarRating({ value }) {
  const rating = Number(value) || 0;
  return (
    <div className="flex items-center gap-0.5">
      {[1, 2, 3, 4, 5].map((i) => (
        <Star
          key={i}
          className={`h-3.5 w-3.5 ${i <= Math.round(rating) ? "fill-amber-400 text-amber-400" : "text-slate-200"}`}
        />
      ))}
      <span className="ml-1 text-sm font-semibold text-slate-700">{rating > 0 ? rating.toFixed(1) : "—"}</span>
    </div>
  );
}

function Trend({ current, snapshots }) {
  if (!snapshots?.length) return null;
  const last = snapshots[snapshots.length - 1];
  const diff = Number(current) - Number(last.rating);
  if (diff === 0) return <Minus className="h-3.5 w-3.5 text-slate-400" />;
  return diff > 0
    ? <TrendingUp className="h-3.5 w-3.5 text-green-500" />
    : <TrendingDown className="h-3.5 w-3.5 text-red-400" />;
}

export default function Competitors() {
  const [competitors, setCompetitors] = useState([]);
  const [myData, setMyData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editId, setEditId] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [compRes, dashRes] = await Promise.all([
        api.get("/competitors"),
        api.get("/dashboard"),
      ]);
      setCompetitors(arr(compRes.data?.competitors));
      setMyData(dashRes.data);
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const openAdd = () => { setForm(emptyForm); setEditId(null); setShowForm(true); };
  const openEdit = (c) => {
    setForm({
      name: c.name || "",
      gbpUrl: c.gbpUrl || "",
      category: c.category || "",
      rating: c.rating || "",
      reviewCount: c.reviewCount || "",
      postFrequency: c.postFrequency || "",
      responseRate: c.responseRate || "",
      notes: c.notes || "",
    });
    setEditId(c.id || c._id);
    setShowForm(true);
  };

  const handleSave = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = {
        ...form,
        rating: form.rating ? Number(form.rating) : 0,
        reviewCount: form.reviewCount ? Number(form.reviewCount) : 0,
      };
      if (editId) {
        const { data } = await api.put(`/competitors/${editId}`, payload);
        setCompetitors((prev) => prev.map((c) => (c.id === data.id ? data : c)));
        toast.success("Competitor updated");
      } else {
        const { data } = await api.post("/competitors", payload);
        setCompetitors((prev) => [data, ...prev]);
        toast.success("Competitor added");
      }
      setShowForm(false);
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (c) => {
    if (!window.confirm(`Remove competitor "${c.name}"?`)) return;
    try {
      await api.delete(`/competitors/${c.id || c._id}`);
      setCompetitors((prev) => prev.filter((x) => x.id !== (c.id || c._id)));
      toast.success("Competitor removed");
    } catch (err) {
      toast.error(apiError(err));
    }
  };

  const myRating = myData?.cards?.googleRating || 0;
  const myReviews = myData?.cards?.totalReviews || 0;
  const myBiz = myData?.client?.businessName || "Your Business";

  // Sort competitors by rating desc for comparison
  const sorted = [...competitors].sort((a, b) => (b.rating || 0) - (a.rating || 0));
  const topComp = sorted[0];

  return (
    <div className="space-y-6">
      <PageHeader
        title="Competitor Insights"
        subtitle="Track how your Google Business Profile compares to nearby competitors"
        icon={Target}
        actions={
          <Button onClick={openAdd} className="btn-primary flex items-center gap-1.5 text-sm">
            <Plus className="h-4 w-4" /> Add Competitor
          </Button>
        }
      />

      {/* My Business vs Top Competitor Summary */}
      {!loading && myData && competitors.length > 0 && topComp && (
        <div className="card-surface p-5">
          <h3 className="mb-4 text-sm font-semibold uppercase tracking-wider text-slate-400">Head-to-Head Comparison</h3>
          <div className="grid grid-cols-3 gap-4 text-center">
            {/* My Business */}
            <div className="rounded-xl bg-brand-50 p-4">
              <p className="text-xs font-semibold text-brand-600 truncate">{myBiz}</p>
              <p className="mt-2 text-3xl font-bold text-slate-900">{myRating > 0 ? myRating.toFixed(1) : "—"}</p>
              <StarRating value={myRating} />
              <p className="mt-1 text-xs text-slate-500">{myReviews} reviews</p>
            </div>
            {/* VS */}
            <div className="flex flex-col items-center justify-center">
              <p className="text-2xl font-black text-slate-300">VS</p>
              <CompareArrow mine={myRating} theirs={topComp.rating} label="rating" />
              <CompareArrow mine={myReviews} theirs={topComp.reviewCount} label="reviews" />
            </div>
            {/* Top Competitor */}
            <div className="rounded-xl bg-slate-50 p-4">
              <p className="text-xs font-semibold text-slate-500 truncate">{topComp.name}</p>
              <p className="mt-2 text-3xl font-bold text-slate-900">{topComp.rating > 0 ? Number(topComp.rating).toFixed(1) : "—"}</p>
              <StarRating value={topComp.rating} />
              <p className="mt-1 text-xs text-slate-500">{topComp.reviewCount || 0} reviews</p>
            </div>
          </div>
        </div>
      )}

      {loading ? (
        <div className="flex items-center justify-center py-20 text-slate-400 text-sm">Loading…</div>
      ) : competitors.length === 0 ? (
        <EmptyState
          icon={Target}
          title="No competitors tracked yet"
          description="Add a competitor by entering their Google Business Profile URL and current stats. You'll be able to compare ratings, review counts, and track changes over time."
        />
      ) : (
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-3">
          {competitors.map((c) => (
            <CompetitorCard
              key={c.id || c._id}
              comp={c}
              myRating={myRating}
              myReviews={myReviews}
              onEdit={openEdit}
              onDelete={handleDelete}
            />
          ))}
        </div>
      )}

      {/* Instructions when there are competitors but no data */}
      {!loading && competitors.length > 0 && (
        <div className="rounded-xl border border-amber-200 bg-amber-50 p-4 text-sm text-amber-800">
          <strong>Tip:</strong> Update competitor ratings and review counts weekly to build trend history. Use the edit button on each card to refresh their latest numbers.
        </div>
      )}

      {/* Add/Edit Modal */}
      {showForm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/30 backdrop-blur-sm" onClick={() => setShowForm(false)} />
          <div className="relative z-10 w-full max-w-lg rounded-2xl bg-white shadow-2xl overflow-y-auto max-h-[90vh]">
            <div className="flex items-center justify-between border-b border-slate-100 px-5 py-4">
              <h2 className="font-bold text-slate-900">{editId ? "Update Competitor" : "Add Competitor"}</h2>
              <button onClick={() => setShowForm(false)} className="rounded-lg p-1.5 hover:bg-slate-100 text-slate-500">
                <X className="h-5 w-5" />
              </button>
            </div>
            <form onSubmit={handleSave} className="space-y-4 p-5">
              <div>
                <label className="form-label">Business Name *</label>
                <input required className="form-input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Competitor's business name" />
              </div>
              <div>
                <label className="form-label">Google Business Profile URL</label>
                <input className="form-input" value={form.gbpUrl} onChange={(e) => setForm({ ...form, gbpUrl: e.target.value })} placeholder="https://maps.google.com/..." />
              </div>
              <div>
                <label className="form-label">Category</label>
                <input className="form-input" value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} placeholder="e.g. Dental Clinic, Salon…" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="form-label">Current Rating</label>
                  <input type="number" step="0.1" min="0" max="5" className="form-input" value={form.rating} onChange={(e) => setForm({ ...form, rating: e.target.value })} placeholder="4.5" />
                </div>
                <div>
                  <label className="form-label">Total Reviews</label>
                  <input type="number" min="0" className="form-input" value={form.reviewCount} onChange={(e) => setForm({ ...form, reviewCount: e.target.value })} placeholder="120" />
                </div>
                <div>
                  <label className="form-label">Post Frequency</label>
                  <input className="form-input" value={form.postFrequency} onChange={(e) => setForm({ ...form, postFrequency: e.target.value })} placeholder="e.g. Weekly, Monthly" />
                </div>
                <div>
                  <label className="form-label">Response Rate</label>
                  <input className="form-input" value={form.responseRate} onChange={(e) => setForm({ ...form, responseRate: e.target.value })} placeholder="e.g. 80%, Rarely" />
                </div>
              </div>
              <div>
                <label className="form-label">Notes</label>
                <textarea rows={2} className="form-input" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} placeholder="Any additional observations…" />
              </div>
              <div className="flex justify-end gap-2 pt-1">
                <Button type="button" variant="outline" onClick={() => setShowForm(false)}>Cancel</Button>
                <Button type="submit" disabled={saving}>{saving ? "Saving…" : editId ? "Update" : "Add Competitor"}</Button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

function CompetitorCard({ comp, myRating, myReviews, onEdit, onDelete }) {
  const ratingDiff = myRating > 0 && comp.rating > 0 ? myRating - comp.rating : null;
  const reviewDiff = myReviews > 0 && comp.reviewCount > 0 ? myReviews - comp.reviewCount : null;

  return (
    <div className="card-surface p-5 flex flex-col gap-4">
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <p className="font-bold text-slate-900 truncate">{comp.name}</p>
          {comp.category && <p className="text-xs text-slate-500">{comp.category}</p>}
        </div>
        <div className="flex gap-1 shrink-0">
          {comp.gbpUrl && (
            <a href={comp.gbpUrl} target="_blank" rel="noopener noreferrer" className="rounded-lg p-1.5 hover:bg-slate-100 text-slate-400 hover:text-brand-600">
              <ExternalLink className="h-4 w-4" />
            </a>
          )}
          <button onClick={() => onEdit(comp)} className="rounded-lg p-1.5 hover:bg-slate-100 text-slate-400 hover:text-slate-700">
            <Edit2 className="h-4 w-4" />
          </button>
          <button onClick={() => onDelete(comp)} className="rounded-lg p-1.5 hover:bg-slate-100 text-slate-400 hover:text-red-500">
            <Trash2 className="h-4 w-4" />
          </button>
        </div>
      </div>

      <div className="grid grid-cols-2 gap-3">
        <Metric label="Rating" value={<StarRating value={comp.rating} />} diff={ratingDiff !== null ? (ratingDiff >= 0 ? `+${ratingDiff.toFixed(1)} vs you` : `${ratingDiff.toFixed(1)} vs you`) : null} diffGood={ratingDiff !== null && ratingDiff >= 0} />
        <Metric label="Reviews" value={<span className="font-semibold text-slate-800">{comp.reviewCount || "—"}</span>} diff={reviewDiff !== null ? (reviewDiff >= 0 ? `You lead by ${reviewDiff}` : `Behind by ${Math.abs(reviewDiff)}`) : null} diffGood={reviewDiff !== null && reviewDiff >= 0} />
        {comp.postFrequency && <Metric label="Post Frequency" value={<span className="text-sm text-slate-700">{comp.postFrequency}</span>} />}
        {comp.responseRate && <Metric label="Response Rate" value={<span className="text-sm text-slate-700">{comp.responseRate}</span>} />}
      </div>

      {comp.notes && <p className="text-xs text-slate-500 italic border-t border-slate-100 pt-3">{comp.notes}</p>}

      {comp.snapshots?.length > 1 && (
        <div className="text-xs text-slate-400 border-t border-slate-100 pt-3">
          <Trend current={comp.rating} snapshots={comp.snapshots.slice(0, -1)} />
          <span className="ml-1">vs last update</span>
        </div>
      )}

      <p className="text-[10px] text-slate-400">Last updated: {new Date(comp.updatedAt || comp.createdAt).toLocaleDateString()}</p>
    </div>
  );
}

function Metric({ label, value, diff, diffGood }) {
  return (
    <div>
      <p className="text-[10px] font-semibold uppercase tracking-wider text-slate-400">{label}</p>
      <div className="mt-0.5">{value}</div>
      {diff && (
        <p className={`mt-0.5 text-[10px] font-medium ${diffGood ? "text-green-600" : "text-red-500"}`}>{diff}</p>
      )}
    </div>
  );
}

function CompareArrow({ mine, theirs, label }) {
  const diff = Number(mine) - Number(theirs);
  const ahead = diff > 0;
  const equal = diff === 0;
  return (
    <div className={`mt-1 flex items-center gap-1 text-xs font-semibold ${equal ? "text-slate-400" : ahead ? "text-green-600" : "text-red-500"}`}>
      {equal ? <Minus className="h-3 w-3" /> : ahead ? <TrendingUp className="h-3 w-3" /> : <TrendingDown className="h-3 w-3" />}
      <span>{equal ? "Tied" : ahead ? `+${Math.abs(diff).toFixed(label === "rating" ? 1 : 0)} ${label}` : `-${Math.abs(diff).toFixed(label === "rating" ? 1 : 0)} ${label}`}</span>
    </div>
  );
}
